import React, { useState, useEffect } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { geoMercator } from "d3-geo";
import { useTranslation } from "react-i18next";

const geoUrl = "/world-110m.json";

// Kontinent-Zuordnung für Länder
const continentMap = {
  "Europa": ["DEU", "GBR", "CHE", "POL", "ITA", "GEO", "GRC", "RUS"],
  "Asien": ["CHN", "JPN", "MYS", "SGP", "THA"],
  "Nordamerika": ["USA", "CAN"],
  "Südamerika": ["BRA", "COL"],
  "Ozeanien": ["AUS", "NZL"],
  "Afrika": ["ZAF"],
};

// Funktion zum Gruppieren der Länder nach Kontinenten
const groupByContinent = (members) => {
  const grouped = {};
  Object.keys(continentMap).forEach(continent => {
    grouped[continent] = [];
  });
  
  members.forEach(member => {
    Object.keys(continentMap).forEach(continent => {
      if (continentMap[continent].includes(member.iso)) {
        grouped[continent].push(member);
      }
    });
  });
  
  // Entferne leere Kontinente
  return Object.fromEntries(
    Object.entries(grouped).filter(([_, countries]) => countries.length > 0)
  );
};

const members = [
  { iso: "DEU", coordinates: [10.4515, 51.1657], flag: "/images/flags/DE.png", capital: "Berlin", population: "83 Millionen", area: "357.022 km²" },
  {
    iso: "CHN",
    coordinates: [104.1954, 35.8617],
    flag: "/images/flags/CN.png",
    capital: "Peking",
    population: "1,4 Milliarden",
    area: "9.596.961 km²"
  },
  {
    iso: "USA",
    coordinates: [-95.7129, 37.0902],
    flag: "/images/flags/US.png",
    capital: "Washington D.C.",
    population: "331 Millionen",
    area: "9.833.520 km²"
  },
  {
    iso: "AUS",
    coordinates: [133.7751, -25.2744],
    flag: "/images/flags/AU.png",
    capital: "Canberra",
    population: "26 Millionen",
    area: "7.692.024 km²"
  },
  {
    iso: "BRA",
    coordinates: [-51.9253, -14.235],
    flag: "/images/flags/BR.png",
    capital: "Brasília",
    population: "215 Millionen",
    area: "8.515.767 km²"
  },
  { iso: "COL", coordinates: [-74.2973, 4.5709], flag: "/images/flags/CO.png", capital: "Bogotá", population: "51 Millionen", area: "1.141.748 km²" },
  {
    iso: "CAN",
    coordinates: [-106.3468, 56.1304],
    flag: "/images/flags/CA.png",
    capital: "Ottawa",
    population: "38 Millionen",
    area: "9.984.670 km²"
  },
  {
    iso: "JPN",
    coordinates: [138.2529, 36.2048],
    flag: "/images/flags/JP.png",
    capital: "Tokio",
    population: "125 Millionen",
    area: "377.975 km²"
  },
  {
    iso: "NZL",
    coordinates: [174.8859, -40.9006],
    flag: "/images/flags/NZ.png",
    capital: "Wellington",
    population: "5 Millionen",
    area: "268.021 km²"
  },
  { iso: "MYS", coordinates: [101.9758, 4.2105], flag: "/images/flags/MY.png", capital: "Kuala Lumpur", population: "33 Millionen", area: "330.803 km²" },
  {
    iso: "GBR",
    coordinates: [-3.435973, 55.378051],
    flag: "/images/flags/GB.png",
    capital: "London",
    population: "67 Millionen",
    area: "243.610 km²"
  },
  { iso: "CHE", coordinates: [8.2275, 46.8182], flag: "/images/flags/CH.png", capital: "Bern", population: "8,7 Millionen", area: "41.290 km²" },
  { iso: "POL", coordinates: [19.1451, 51.9194], flag: "/images/flags/PL.png", capital: "Warschau", population: "38 Millionen", area: "312.696 km²" },
  { iso: "SGP", coordinates: [103.8198, 1.3521], flag: "/images/flags/SG.png", capital: "Singapur", population: "5,7 Millionen", area: "719 km²" },
  { iso: "ITA", coordinates: [12.5674, 41.8719], flag: "/images/flags/IT.png", capital: "Rom", population: "59 Millionen", area: "301.340 km²" },
  {
    iso: "ZAF",
    coordinates: [22.9375, -30.5595],
    flag: "/images/flags/ZA.png",
    capital: "Kapstadt / Pretoria",
    population: "60 Millionen",
    area: "1.219.090 km²"
  },
  { iso: "GEO", coordinates: [43.3569, 42.3154], flag: "/images/flags/GE.png", capital: "Tiflis", population: "3,7 Millionen", area: "69.700 km²" },
  { iso: "GRC", coordinates: [21.8243, 39.0742], flag: "/images/flags/GR.png", capital: "Athen", population: "10,7 Millionen", area: "131.957 km²" },
  { iso: "THA", coordinates: [100.9925, 15.87], flag: "/images/flags/TH.png", capital: "Bangkok", population: "70 Millionen", area: "513.120 km²" },
  { iso: "RUS", coordinates: [105.3188, 61.524], flag: "/images/flags/RU.png", capital: "Moskau", population: "146 Millionen", area: "17.098.242 km²" },
];

function ChinWooWorldMap() {
  const { t } = useTranslation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [highlightedCountry, setHighlightedCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const groupedMembers = groupByContinent(members);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    
    return () => observer.disconnect();
  }, []);


  // Primary color values for map styling (using Tailwind's blue-500/blue-400 as base)
  const primaryColor = isDarkMode ? "96, 165, 250" : "59, 130, 246"; // blue-400 for dark, blue-500 for light
  const nonMemberColor = isDarkMode ? "148, 163, 184" : "148, 163, 184"; // slate-400 for both

  // Get selected member for info box
  const selectedMember = selectedCountry ? members.find(m => m.iso === selectedCountry) : null;

  return (
    <section className="pt-12 pb-12 bg-slate-50/50 dark:bg-slate-900 transition-colors relative z-10" id="chinwoo-world-map">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("chinwooWorldMap.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
            {t("chinwooWorldMap.description")}
          </p>
        </div>

        {/* Split View: Countries List + Map */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Countries List (Left) - 1/3 width */}
          <div className="lg:col-span-1 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t("chinwooWorldMap.memberCountries", "Mitgliedsländer")}
              </h3>
            </div>
            <div className="overflow-y-auto max-h-[600px] p-4">
              {Object.entries(groupedMembers).map(([continent, countries], index) => (
                <div key={continent} className={`${index > 0 ? 'pt-6 mt-6 border-t border-slate-200 dark:border-slate-700' : ''}`}>
                  <h4 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <i className="fas fa-globe text-primary dark:text-primary-light"></i>
                    {continent}
                  </h4>
                  <div className="space-y-2">
                    {countries.map((member) => {
                      const countryNameRaw = t(
                        `chinwooWorldMap.countries.${member.iso}`,
                        member.iso
                      );
                      // Stelle sicher, dass wir einen String bekommen, kein Objekt
                      const countryName = typeof countryNameRaw === 'string' ? countryNameRaw : member.iso;
                      const isHighlighted = highlightedCountry === member.iso;
                      const isSelected = selectedCountry === member.iso;
                      
                      return (
                        <button
                          key={member.iso}
                          onClick={() => setSelectedCountry(member.iso)}
                          onMouseEnter={() => setHighlightedCountry(member.iso)}
                          onMouseLeave={() => setHighlightedCountry(null)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-200 text-left ${
                            isSelected
                              ? "bg-primary/10 dark:bg-primary-light/20 border-primary dark:border-primary-light shadow-sm"
                              : isHighlighted
                              ? "bg-slate-50 dark:bg-slate-700/50 border-slate-300 dark:border-slate-600 shadow-sm"
                              : "bg-transparent border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600"
                          }`}
                        >
                          <img
                            src={member.flag}
                            alt={`${countryName} flag`}
                            className={`w-10 h-10 object-cover rounded-full border-2 shadow-sm transition-all duration-300 flex-shrink-0 ${
                              isSelected || isHighlighted
                                ? "border-primary dark:border-primary-light scale-105"
                                : "border-slate-200 dark:border-slate-600"
                            }`}
                          />
                          <span className={`text-sm font-medium flex-1 ${
                            isSelected || isHighlighted
                              ? "text-primary dark:text-primary-light"
                              : "text-slate-900 dark:text-slate-100"
                          }`}>
                            {countryName}
                          </span>
                          {(isSelected || isHighlighted) && (
                            <i className="fas fa-map-marker-alt text-primary dark:text-primary-light text-xs"></i>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Map Container (Right) - 2/3 width */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-4 md:p-6 lg:p-8">
            {/* Map Wrapper with Info Box */}
            <div className="relative w-full rounded-lg chinwoo-world-map__map-wrapper flex flex-col">
              {/* Map */}
              <div className="relative w-full overflow-hidden rounded-lg mb-6">
                <ComposableMap
                  projectionConfig={{ scale: 150 }}
                  width={800}
                  height={400}
                  className="w-full h-auto drop-shadow-sm"
                >
                  <Geographies geography={geoUrl}>
                    {({ geographies }) => {
                      // Filtere doppelte Länder - verwende nur das erste Geo-Objekt pro Land
                      const seenCountries = new Set();
                      const filteredGeographies = geographies.filter((geo) => {
                        const countryISO = geo.properties.adm0_a3;
                        if (seenCountries.has(countryISO)) {
                          return false;
                        }
                        seenCountries.add(countryISO);
                        return true;
                      });

                      return filteredGeographies.map((geo) => {
                        const countryISO = geo.properties.adm0_a3;
                        const member = members.find((m) => m.iso === countryISO);
                        // Nur für Mitgliedstaaten Hover-Effekte
                        const isHighlighted = member && highlightedCountry === countryISO;
                        const isSelected = member && selectedCountry === countryISO;

                        // Für Nicht-Mitgliedstaaten: keine Mouse-Events und konstante Styles
                        // Hover und Pressed müssen explizit gesetzt werden, damit sie sich nicht ändern
                        const nonMemberFill = `rgba(${nonMemberColor}, ${isDarkMode ? 0.15 : 0.1})`;
                        const nonMemberStroke = `rgba(${nonMemberColor}, ${isDarkMode ? 0.4 : 0.3})`;
                        const nonMemberStyle = {
                          default: {
                            fill: nonMemberFill,
                            stroke: nonMemberStroke,
                            strokeWidth: 1.2,
                            outline: "none",
                            filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05))",
                            transform: "none",
                            transformOrigin: "center center",
                            transition: "none",
                            cursor: "default",
                          },
                          hover: {
                            fill: nonMemberFill,
                            stroke: nonMemberStroke,
                            strokeWidth: 1.2,
                            outline: "none",
                            filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05))",
                            transform: "none",
                            transformOrigin: "center center",
                            transition: "none",
                            cursor: "default",
                          },
                          pressed: {
                            fill: nonMemberFill,
                            stroke: nonMemberStroke,
                            strokeWidth: 1.2,
                            outline: "none",
                            filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05))",
                            transform: "none",
                            transformOrigin: "center center",
                            transition: "none",
                            cursor: "default",
                          },
                        };

                        // Für Mitgliedstaaten: vollständige Styles mit Hover
                        const memberStyle = {
                          default: {
                            fill: `rgba(${primaryColor}, ${isSelected ? (isDarkMode ? 0.65 : 0.6) : isHighlighted ? (isDarkMode ? 0.6 : 0.55) : (isDarkMode ? 0.4 : 0.35)})`,
                            stroke: `rgba(${primaryColor}, ${isSelected ? (isDarkMode ? 1 : 0.9) : isHighlighted ? (isDarkMode ? 0.9 : 0.8) : (isDarkMode ? 0.6 : 0.5)})`,
                            strokeWidth: isSelected ? 2.5 : (isHighlighted ? 2 : 1.2),
                            outline: "none",
                            filter: isSelected 
                              ? `drop-shadow(0 10px 15px rgba(${primaryColor}, 0.4))`
                              : isHighlighted
                              ? `drop-shadow(0 4px 6px rgba(${primaryColor}, 0.3))`
                              : `drop-shadow(0 4px 6px rgba(${primaryColor}, 0.2))`,
                            transform: isSelected ? "scale(1.05)" : (isHighlighted ? "scale(1.02)" : "none"),
                            transformOrigin: "center center",
                            transition: "all 0.3s ease",
                            cursor: "pointer",
                          },
                          hover: {
                            fill: `rgba(${primaryColor}, ${isDarkMode ? 0.6 : 0.55})`,
                            stroke: `rgba(${primaryColor}, ${isDarkMode ? 0.9 : 0.8})`,
                            strokeWidth: 2,
                            filter: `drop-shadow(0 4px 6px rgba(${primaryColor}, 0.3))`,
                            transform: "scale(1.02)",
                          },
                          pressed: {
                            fill: `rgba(${primaryColor}, ${isDarkMode ? 0.65 : 0.6})`,
                            stroke: `rgba(${primaryColor}, ${isDarkMode ? 1 : 0.9})`,
                            strokeWidth: 2.5,
                            filter: `drop-shadow(0 10px 15px rgba(${primaryColor}, 0.4))`,
                            transform: "scale(1.05)",
                          },
                        };

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            onClick={member ? () => {
                              setSelectedCountry(countryISO);
                            } : undefined}
                            onMouseEnter={member ? () => {
                              setHighlightedCountry(countryISO);
                            } : undefined}
                            onMouseLeave={member ? () => {
                              setHighlightedCountry(null);
                            } : undefined}
                            style={member ? memberStyle : nonMemberStyle}
                          />
                        );
                      });
                    }}
                  </Geographies>
                </ComposableMap>
              </div>
              
              {/* Country Info Box */}
              {selectedMember && (() => {
                const countryNameRaw = t(`chinwooWorldMap.countries.${selectedMember.iso}`, selectedMember.iso);
                const countryName = typeof countryNameRaw === 'string' ? countryNameRaw : selectedMember.iso;
                return (
                <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-start gap-4">
                  <img
                    src={selectedMember.flag}
                    alt={`${countryName} flag`}
                    className="w-16 h-16 object-cover rounded-full border-2 border-primary/30 dark:border-primary-light/30 shadow-md flex-shrink-0"
                  />
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
                        {countryName}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                            <i className="fas fa-building text-primary dark:text-primary-light text-sm"></i>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                              Hauptstadt
                            </p>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {selectedMember.capital}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                            <i className="fas fa-users text-primary dark:text-primary-light text-sm"></i>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                              Einwohner
                            </p>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {selectedMember.population}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-light/20 flex items-center justify-center">
                            <i className="fas fa-map text-primary dark:text-primary-light text-sm"></i>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                              Fläche
                            </p>
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {selectedMember.area}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ChinWooWorldMap;
